import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "../../store";
import { DeliveryListResponse, RequestDeliveryParams } from "./types";
import { exportDeliveryApi, getDeliveryListApi } from "./api";

type DeliveryListType = DeliveryListResponse & {
  loading: boolean;
  error: string | null;
  success: string | null;
  currentParams: Partial<RequestDeliveryParams> | null;
  exporting: { loading: boolean; error: string | null };
};

const initialState: DeliveryListType = {
  items: null,
  loading: false,
  error: null,
  success: null,
  total: 0,
  page: 0,
  size: 0,
  currentParams: null,
  exporting: { loading: false, error: null },
};

const deliveryList = createSlice({
  name: "deliveryList",
  initialState: initialState,
  reducers: {
    getDeliveryStart(state: DeliveryListType) {
      state.loading = true;
      state.error = null;
    },
    getDeliverySuccess(
      state: DeliveryListType,
      action: PayloadAction<
        DeliveryListResponse & { params?: RequestDeliveryParams }
      >,
    ) {
      const { items, total, page, size, params } = action.payload;
      state.items = items;
      state.total = total;
      state.page = page;
      state.size = size;
      state.loading = false;
      if (params) {
        const { page, size, ...exportParams } = params;
        state.currentParams = exportParams;
      }
    },
    getDeliveryFailure(state: DeliveryListType, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
      state.items = null;
    },
    getExportFailure(state: DeliveryListType, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    hideWarning(state: DeliveryListType) {
      state.error = null;
    },
    cleanPackage(state: DeliveryListType) {
      state.items = null;
    },
    startExport(state: DeliveryListType) {
      state.exporting = {
        loading: true,
        error: null,
      };
    },
    successExport(state: DeliveryListType) {
      state.exporting.loading = false;
      state.exporting.error = null;
    },
  },
});

export const {
  getDeliveryStart,
  hideWarning,
  cleanPackage,
  getDeliverySuccess,
  getDeliveryFailure,
  getExportFailure,
  startExport,
  successExport,
} = deliveryList.actions;

export default deliveryList.reducer;

export const fetchDeliveryList =
  (params: RequestDeliveryParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getDeliveryStart());
      const data = await getDeliveryListApi(params);
      dispatch(getDeliverySuccess({ ...data, params }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getDeliveryFailure(error));
    }
  };

export const exportDelivery =
  (params?: Partial<RequestDeliveryParams>): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport());
      const { file, filename } = await exportDeliveryApi(params);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch {
      dispatch(getDeliveryFailure("Downloading failed. Try again later."));
    }
  };
